import { get, post } from "../helpers/directApi";

/**
 * Thin wrapper over the ForecastNest billing + research API.
 *
 * Uses directApi rather than APIClient. Billing is the one area where the
 * reason a call failed decides what to tell the member — "you have no
 * subscription to manage", "payments are not configured", "that symbol is
 * already yours" are three different answers — and the shared client's global
 * interceptor rejects with a bare string, which collapses all of them into one
 * generic sentence. Each call still resolves to the response body, so this is a
 * change to the failure path only.
 */

// Current subscription state + entitlements for the logged-in user.
export const getSubscriptionStatus = () => get("/api/billing/status");

// Create a Stripe checkout session; resolves to { url }.
// returnTo is where Stripe sends the member afterwards — the server validates
// it and falls back to a default, so a bad value cannot become a redirect.
export const startCheckout = (returnTo) =>
  post("/api/billing/checkout", returnTo ? { returnTo } : {});

// A link into Stripe's own subscription management — cancelling, changing a
// card, reading invoices. Minted per click rather than stored: the old
// Lemon Squeezy portal URL was kept in the database and went stale.
export const openBillingPortal = () => post("/api/billing/portal", {});

// Ask the server to re-read Stripe before answering /status.
//
// Used when the member returns from the customer portal: they may have just
// cancelled, and the webhook that would tell us is a separate delivery that has
// not necessarily landed yet. Resolves to { refreshed } and is safe to ignore —
// the page falls back to the mirror row.
export const refreshSubscription = () => post("/api/billing/refresh", {});

// A refund on this account, if there has been one recently.
//
// There is no requestRefund to go with it: refunds are not self-serve, and the
// policy no longer promises them. This is a receipt, not a request — when a
// refund IS issued by hand, the member needs to see that it happened and that
// the money takes days to arrive, rather than finding their subscription
// mysteriously gone. Resolves to { last_refund, notice_days }.
export const getRefundNotice = () => get("/api/billing/refund");

// No setResearchSymbol: the endpoint is gone with the entitlement it set.
//
// getTopResearch and getSymbolResearch are gone too. Both served system
// forecasts, which are internal now — the first was the free funnel showing
// three of them, and a teaser for something nobody can buy is just a leak.
